import { blogUrl } from "@repo/fika-blog/src/blogUrl"
import { postContentClass } from "@repo/fika-blog/src/classes"
import { postClass, postContainerClass } from "@repo/fika-blog/src/content.css"
import { extensions } from "@repo/fika-blog/src/extensions"
import { renderToHTMLString } from "@repo/fika-blog/src/renderer/pm/html-string/html-string"
import type { IconName } from "@repo/fika-design-system/icon"
import { createMemo, createSignal, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { LinkButton } from "@/designSystem/components/button"
import { Form } from "@/designSystem/components/form"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { Switch } from "@/designSystem/components/switch"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/textField"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ImageUpload } from "@/designSystem/patterns/image-upload"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/getTitle"
import { generateUrl } from "@/lib/slug"
import { createBlogRouter, type SectionName } from "@/routes/posts-router"
import {
  bodyClass,
  browserClass,
  headerAddressClass,
  headerAddressLinkClass,
  headerClass,
  iconsClass,
  iframeClass,
  sectionFormClass,
  wrapperClass,
} from "./settings.css"

type Preview = "blog" | "post"

export function PostsSettings() {
  return <Settings />
}

type Props = {
  topLevel?: boolean
}
function Settings(props: Props) {
  const { LL } = useI18nContext()
  const { session, replicache, user } = useAppContext()
  const isEditing = createMemo(() => user.name)
  const { paths } = createBlogRouter()

  const buttons = createMemo(() => {
    if (props.topLevel) return []

    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
    ]
  })

  const [formName, setFormName] = createSignal(user.name || "")
  const [formTitle, setFormTitle] = createSignal(user.title || "")
  const [formDescription, setFormDescription] = createSignal(
    user.description || "",
  )
  const [formBookmarks, setFormBookmarks] = createSignal(user.share_bookmarks)

  const defaultBranding = createMemo(() => {
    return {
      logo: user.logo_url,
      wordmark: user.wordmark_url,
      welcomeImage: user.welcome_image_url,
    }
  })
  const [formLogo, setFormLogo] = createSignal(defaultBranding().logo)
  const [formWordmark, setFormWordmark] = createSignal(
    defaultBranding().wordmark,
  )
  const [formWelcomeImage, setFormWelcomeImage] = createSignal(
    defaultBranding().welcomeImage,
  )

  const brandingDirty = createMemo(
    () =>
      formLogo() !== defaultBranding().logo ||
      formWordmark() !== defaultBranding().wordmark ||
      formWelcomeImage() !== defaultBranding().welcomeImage,
  )

  const validName = createMemo(() => {
    const name = formName()
    if (!name.length) return false

    return /^[a-zA-Z_][a-zA-Z0-9_-]{3,14}$/.test(name)
  })

  const dirtySettings = createMemo(
    () =>
      formName() !== user.name ||
      formTitle() !== user.title ||
      formDescription() !== user.description,
  )
  const disabledSettings = createMemo(
    () =>
      !dirtySettings() ||
      !validName() ||
      [formTitle(), formDescription()].every(field => field.length === 0),
  )

  const disabledAutomation = createMemo(
    () => formBookmarks() === user.share_bookmarks,
  )

  function getSectionHTML(section?: string | null) {
    if (!section) return null

    return renderToHTMLString({
      extensions: extensions({ dropcursor: false }),
      content: JSON.parse(section),
    })
  }

  const getFooterHTML = createMemo(() => getSectionHTML(user.blog_footer))
  const getHeaderHTML = createMemo(() => getSectionHTML(user.blog_header))

  async function onSubmitSettings() {
    await replicache.mutate.updateUser({
      id: session.userID,
      name: formName(),
      title: formTitle(),
      description: formDescription(),
    })
  }

  async function onSubmitBranding() {
    await replicache.mutate.updateUser({
      id: session.userID,
      logo_url: formLogo(),
      wordmark_url: formWordmark(),
      welcome_image_url: formWelcomeImage(),
    })
  }

  async function onSubmitAutomation() {
    await replicache.mutate.updateUser({
      id: session.userID,
      share_bookmarks: formBookmarks(),
    })
  }

  return (
    <MainLayout
      header={
        <Navigation hue="amber" buttons={buttons()} title="blog settings" />
      }
      rightPanel={
        <Browser loading={!user.backend} name={user.name ?? formName()} />
      }
    >
      <Box gap="24">
        <Form
          title="settings"
          description="set your blog's username, title and description: it will be used to identify your blog and to generate the url"
          hue="amber"
          iconName="user"
          onSubmit={onSubmitSettings}
        >
          <TextField
            value={formName()}
            onChange={setFormName}
            validationState={validName() ? "valid" : "invalid"}
          >
            <TextField.Label>username</TextField.Label>
            <TextField.Input placeholder="your-user-name" />
            <Show
              when={!validName()}
              fallback={
                <TextField.Description>
                  this is the address where your blog will be available
                </TextField.Description>
              }
            >
              <TextField.ErrorMessage>
                addresses can only contain lowercase letters, numbers, and
                hyphens. no spaces or special characters are allowed.
              </TextField.ErrorMessage>
            </Show>
          </TextField>
          <TextField value={formTitle()} onChange={setFormTitle}>
            <TextField.Label>title</TextField.Label>
            <TextField.Input placeholder="your-blog-title" />
          </TextField>
          <TextField value={formDescription()} onChange={setFormDescription}>
            <TextField.Label>description</TextField.Label>
            <TextField.Input placeholder="your-blog-description" />
          </TextField>
          <Form.Save
            hue="amber"
            iconName="user"
            disabled={disabledSettings()}
          />
        </Form>
        <Form
          title="Branding"
          description="Add your own branding to your blog and emails"
          hue="amber"
          iconName="palette"
          onSubmit={onSubmitBranding}
        >
          <TextField>
            <TextField.Label>logo</TextField.Label>
            <ImageUpload
              text="upload logo"
              imageSrc={formLogo()}
              onUpload={setFormLogo}
              hue="amber"
            />
            <TextField.Description>
              This will be shown in the top of your blog, and in the emails.
              Recommended size: 200 x 200 pixels.
            </TextField.Description>
          </TextField>
          <TextField>
            <TextField.Label>wordmark</TextField.Label>
            <ImageUpload
              text="upload wordmark"
              imageSrc={formWordmark()}
              onUpload={setFormWordmark}
              hue="amber"
            />
            <TextField.Description>
              This will be shown instead of your blog name in the top of your
              blog. Recommended size: 1000 x 200 pixels.
            </TextField.Description>
          </TextField>
          <TextField>
            <TextField.Label>subscribe image</TextField.Label>
            <ImageUpload
              text="upload subscribe image"
              imageSrc={formWelcomeImage()}
              onUpload={setFormWelcomeImage}
              hue="amber"
            />
            <TextField.Description>
              This will be shown in the subscribe form. Recommended size: 600 x
              600 pixels.
            </TextField.Description>
          </TextField>
          <Form.Save
            hue="amber"
            iconName="palette"
            disabled={!brandingDirty()}
          />
        </Form>
        <Show when={isEditing()}>
          <Form
            title="include bookmarks"
            description="every time you bookmark a story, it will be published on your blog"
            hue="amber"
            iconName="book-marked"
            onSubmit={onSubmitAutomation}
          >
            <Switch
              label="include bookmarks"
              checked={formBookmarks()}
              onChange={setFormBookmarks}
              hue="amber"
            />
            <Form.Save
              hue="amber"
              iconName="book-marked"
              disabled={disabledAutomation()}
            />
          </Form>
          <SectionForm
            section={getHeaderHTML()}
            name="header"
            iconName="panel-top"
          />
          <SectionForm
            section={getFooterHTML()}
            name="footer"
            iconName="panel-bottom"
          />
        </Show>
      </Box>
    </MainLayout>
  )
}

type SectionFormProps = {
  section: string | null
  name: SectionName
  iconName: IconName
}
function SectionForm(props: SectionFormProps) {
  const { paths } = createBlogRouter()

  return (
    <Section
      title={props.name}
      description={`add a personalized ${props.name} to each one of your posts`}
      hue="amber"
      iconName={props.iconName}
      direction="column"
    >
      <Box gap="16">
        <Show
          when={props.section}
          fallback={<Well hue="sand">you don't have a {props.name} yet</Well>}
        >
          {sectionHTML => (
            <div class={sectionFormClass}>
              <div class={postContainerClass}>
                <div class={postClass}>
                  <div class={postContentClass} innerHTML={sectionHTML()} />
                </div>
              </div>
            </div>
          )}
        </Show>
        <Box alignSelf="flex-end">
          <LinkButton
            href={paths().settings().section(props.name)}
            hue="amber"
            iconName={props.section ? "pencil" : "plus"}
          >
            {props.section ? "edit" : "create"}
          </LinkButton>
        </Box>
      </Box>
    </Section>
  )
}

type BrowserProps = {
  name: string
  loading: boolean
}
function Browser(props: BrowserProps) {
  const { db, user } = useAppContext()
  const [preview, setPreview] = createSignal<Preview>("blog")
  const updatedAt = createMemo(() => user.updated_at)

  const posts = createMemo(() =>
    db.post
      .sortBy("published_at", "desc")
      .filter(p => !!p.published_at)
      .slice(0, 2),
  )
  const lastPost = createMemo(() => posts()[0])

  const href = createMemo(() => {
    const baseUrl = import.meta.env.VITE_FIKA_PUBLIC_URL
    const url = blogUrl(baseUrl, props.name)
    if (preview() === "blog") {
      return url
    }
    const post = lastPost()
    if (!post) return null

    const postUrl = generateUrl(getTitle(post.title), post.id)

    return `${url}/${postUrl}`
  })
  const values = createMemo(() => {
    const values = [{ value: "blog", title: "blog" }]
    if (!lastPost()) return values

    return values.concat([{ value: "post", title: "post" }])
  })

  return (
    <Box class={wrapperClass}>
      <Box
        alignItems="center"
        gap="8"
        flexDirection="row"
        justifyContent="center"
      >
        <Text color="sand600" fontSize="s" textAlign="center" fontWeight="500">
          this is how your
        </Text>
        <Mini.Menu>
          <Mini.ToggleGroup
            value={preview()}
            onChange={val => (val ? setPreview(val as Preview) : null)}
            values={values()}
          />
        </Mini.Menu>
        <Text color="sand600" fontSize="s" textAlign="center" fontWeight="500">
          looks like
        </Text>
      </Box>
      <div class={browserClass}>
        <div class={headerClass}>
          <Icon
            name="arrow-left"
            size="18"
            color="sand600"
            class={iconsClass}
          />
          <Icon
            name="arrow-right"
            size="18"
            color="sand600"
            class={iconsClass}
          />
          <Icon name="rotate-cw" size="18" color="sand600" class={iconsClass} />
          <Show when={href()}>
            {href => (
              <div class={headerAddressClass}>
                <a
                  href={href()}
                  target="_blank"
                  rel="noreferrer"
                  class={headerAddressLinkClass}
                >
                  {href().replace(/.*:\/\//, "")}
                </a>
              </div>
            )}
          </Show>
        </div>
        <div class={bodyClass}>
          <Show
            when={!props.loading}
            fallback={
              <Box padding="16" alignItems="center" justifyContent="center">
                <Text
                  color="sand600"
                  fontSize="s"
                  textAlign="center"
                  fontWeight="500"
                >
                  loading...
                </Text>
              </Box>
            }
          >
            <Show when={href()}>
              {href => (
                <iframe
                  class={iframeClass}
                  src={`${href()}?updatedAt=${updatedAt()}`}
                  title="blog preview"
                />
              )}
            </Show>
          </Show>
        </div>
      </div>
    </Box>
  )
}
